import { useState } from "react";
import { Landmark, Loader2, Zap, Coins, CreditCard, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmt } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { CoinIcon } from "@/components/Brand";

const inputCls = "w-full bg-[#0B0E14] border border-white/10 rounded-xl px-4 py-3 outline-none focus:border-[#10B981] focus:ring-2 focus:ring-[#10B981]/20 transition-[border-color,box-shadow] placeholder:text-gray-600";
const MIN = 50;

export function RedeemTab() {
  const { user, refresh, setSuccess, closeStore } = useApp();
  const cards = user?.cards || [];
  const [method, setMethod] = useState(cards.length ? "card" : "usdc");
  const [f, setF] = useState({ amount: "", account_name: "", account_number: "", wallet_address: "", card_last4: cards[0]?.last4 || "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const amt = Number(f.amount || 0);
  const redeemable = user?.sc_redeemable ?? 0;
  const locked = user?.playthrough_remaining ?? 0;

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/store/redeem", { ...f, amount: amt, method });
      await refresh();
      closeStore();
      const now = new Date();
      if (data.instant) {
        setSuccess({
          title: "Withdrawal Sent", message: `Payout of $${amt.toFixed(2)} was processed instantly.`, sub: `On its way to ${data.destination}.`, okLabel: "DONE",
          receipt: [["Date", `${now.toLocaleDateString()} · ${now.toLocaleTimeString()}`], ["Destination", data.destination], ["Payout Ref", data.payout_ref], ["Amount", `$${amt.toFixed(2)}`, "sc-text"], ["Status", "✓ PAID · SENT", "text-[#c4b5fd]"]],
        });
      } else {
        setSuccess({ title: "Withdrawal Submitted", message: `$${amt.toFixed(2)} is being processed.`, sub: "Exceeds today's $5,000 instant limit — paid same business day.", okLabel: "DONE", receipt: [["Destination", data.destination], ["Amount", `$${amt.toFixed(2)}`, "sc-text"], ["Status", "PROCESSING", "text-[#F5A524]"]] });
      }
      setF({ amount: "", account_name: "", account_number: "", wallet_address: "", card_last4: cards[0]?.last4 || "" });
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const valid = amt >= MIN && amt <= redeemable &&
    (method !== "usdc" || f.wallet_address.length >= 12) &&
    (method !== "bank" || f.account_number.length >= 4) &&
    (method !== "card" || f.card_last4);

  return (
    <div className="space-y-5" data-testid="redeem-tab">
      <div className="rounded-2xl bg-gradient-to-br from-[#10B981]/20 to-[#1F2633] border border-[#10B981]/20 p-5">
        <div className="text-xs uppercase tracking-widest text-gray-400">Available to redeem</div>
        <div className="mt-1 flex items-center gap-2 font-display text-3xl font-black sc-text" data-testid="redeem-available"><CoinIcon cur="sc" size={26} />{fmt(redeemable, "sc")}</div>
        <div className="mt-1 text-xs text-gray-400">1 SC = $1.00 · Min {MIN} SC · Instant up to $5,000/day</div>
        {locked > 0 && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-[#0B0E14]/60 px-3 py-2 text-xs text-[#F5A524]" data-testid="playthrough-locked">
            <Lock size={13} />{fmt(locked, "sc")} SC locked — play it through to unlock
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-[#10B981]/10 border border-[#10B981]/20 px-3 py-2 text-xs sc-text font-semibold" data-testid="instant-payout-note">
        <Zap size={14} /> Instant same-day payouts — funds sent within minutes
      </div>
      <div className="grid grid-cols-3 gap-2">
        {[["card", "Card", CreditCard], ["usdc", "USDC", Coins], ["bank", "Bank", Landmark]].map(([k, l, I]) => (
          <button key={k} data-testid={`redeem-method-${k}`} onClick={() => setMethod(k)} className={`flex flex-col items-center gap-1 rounded-xl border p-3 font-bold text-sm transition-colors ${method === k ? "border-[#10B981] bg-[#10B981]/10" : "border-white/10 bg-[#1F2633] text-gray-400"}`}>
            <I size={18} />{l}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        <div className="relative">
          <input data-testid="redeem-amount" type="number" min={MIN} placeholder={`Amount (min ${MIN} SC)`} value={f.amount} onChange={set("amount")} className={inputCls} />
          <button data-testid="redeem-max-btn" onClick={() => setF((s) => ({ ...s, amount: String(Math.floor(redeemable * 100) / 100) }))} className="absolute right-2 top-2 rounded-lg bg-[#1F2633] px-3 py-1.5 text-xs font-bold sc-text">MAX</button>
        </div>
        {method === "card" && (
          cards.length ? (
            <div className="space-y-2" data-testid="redeem-card-list">
              {cards.map((c) => (
                <button key={c.id} data-testid={`redeem-card-${c.last4}`} onClick={() => setF((s) => ({ ...s, card_last4: c.last4 }))} className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 text-sm transition-colors ${f.card_last4 === c.last4 ? "border-[#10B981] bg-[#10B981]/10" : "border-white/10 bg-[#1F2633]"}`}>
                  <span className="flex items-center gap-2"><CreditCard size={16} />{c.brand} •••• {c.last4}</span>
                  <span className="text-xs text-gray-500">payout to card</span>
                </button>
              ))}
            </div>
          ) : <div className="text-xs text-gray-500">Make a purchase first to add a card you can withdraw to.</div>
        )}
        {method === "usdc" && <input data-testid="redeem-wallet" placeholder="USDC wallet address" value={f.wallet_address} onChange={set("wallet_address")} className={inputCls} />}
        {method === "bank" && (
          <>
            <input data-testid="redeem-account-name" placeholder="Account holder name" value={f.account_name} onChange={set("account_name")} className={inputCls} />
            <input data-testid="redeem-account-number" inputMode="numeric" placeholder="Account number" value={f.account_number} onChange={set("account_number")} className={inputCls} />
          </>
        )}
      </div>
      <button data-testid="redeem-submit-btn" disabled={busy || !valid} onClick={submit} className="w-full flex items-center justify-center gap-2 rounded-xl bg-[#10B981] hover:bg-[#14CC8F] text-[#0B0E14] disabled:opacity-40 py-4 font-display text-lg font-extrabold transition-[background-color,opacity]">
        {busy && <Loader2 className="animate-spin" size={20} />} Withdraw {amt >= MIN ? `$${amt.toFixed(2)}` : ""}
      </button>
      <p className="text-xs text-gray-500">Withdrawals up to $5,000 per day are paid instantly. Larger payouts clear the same business day.</p>
    </div>
  );
}
